import { mockApi } from './mockApi';

// Environment configuration
// Default to true if no environment variables are set
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA !== 'false' && (!import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL === '');

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Generic API request function
async function apiRequest<T>(
  method: string,
  endpoint: string,
  data?: unknown,
  options?: RequestInit
): Promise<T> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    ...options,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${response.status}: ${text}`);
  }

  return response.json();
}

// API service class
class ApiService {
  private useMockData: boolean;

  constructor() {
    this.useMockData = USE_MOCK_DATA;
  }

  // Generic GET request
  async get<T>(endpoint: string): Promise<T> {
    if (this.useMockData) {
      return mockApi.get<T>(endpoint);
    }
    return apiRequest<T>('GET', endpoint);
  }

  // Generic POST request
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    if (this.useMockData) {
      return mockApi.post<T>(endpoint, data);
    }
    return apiRequest<T>('POST', endpoint, data);
  }

  // Generic PUT request
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    if (this.useMockData) {
      return mockApi.put<T>(endpoint, data);
    }
    return apiRequest<T>('PUT', endpoint, data);
  }

  // Generic DELETE request
  async delete<T>(endpoint: string): Promise<T> {
    if (this.useMockData) {
      return mockApi.delete<T>(endpoint);
    }
    return apiRequest<T>('DELETE', endpoint);
  }

  // Auth specific methods
  async login(credentials: { email: string; password: string }) {
    if (this.useMockData) {
      return mockApi.login(credentials);
    }
    const response = await apiRequest<{ token: string; user: any }>('POST', '/api/auth/login', credentials);
    return response;
  }

  async logout() {
    localStorage.removeItem("token");
    if (!this.useMockData) {
      await apiRequest('POST', '/api/auth/logout');
    }
  }

  async getCurrentUser() {
    if (this.useMockData) {
      return mockApi.getCurrentUser();
    }
    return apiRequest<any>('GET', '/api/auth/me');
  }

  // Dashboard methods
  async getDashboardStats() {
    return this.get('/api/dashboard/stats');
  }

  // Drivers methods
  async getDrivers() {
    return this.get('/api/drivers');
  }

  async updateDriver(id: number, data: any) {
    return this.put(`/api/drivers/${id}`, data);
  }

  async deleteDriver(id: number) {
    return this.delete(`/api/drivers/${id}`);
  }

  // Riders methods
  async getRiders() {
    return this.get('/api/riders');
  }

  async updateRider(id: number, data: any) {
    return this.put(`/api/riders/${id}`, data);
  }

  async deleteRider(id: number) {
    return this.delete(`/api/riders/${id}`);
  }

  // Trips methods
  async getTrips() {
    return this.get('/api/trips');
  }

  async updateTrip(id: number, data: any) {
    return this.put(`/api/trips/${id}`, data);
  }

  // Vehicles methods
  async getVehicles() {
    return this.get('/api/vehicles');
  }

  async updateVehicle(id: number, data: any) {
    return this.put(`/api/vehicles/${id}`, data);
  }

  // Complaints methods
  async getComplaints() {
    return this.get('/api/complaints');
  }

  async updateComplaint(id: number, data: any) {
    return this.put(`/api/complaints/${id}`, data);
  }

  // Cities methods
  async getCities() {
    return this.get('/api/cities');
  }

  async updateCity(id: number, data: any) {
    return this.put(`/api/cities/${id}`, data);
  }

  // Vehicle categories methods
  async getVehicleCategories() {
    return this.get('/api/vehicle-categories');
  }

  // Finance methods
  async getFinanceData() {
    return this.get('/api/finance');
  }

  // Hotel Booking Platform methods
  async getHotels() {
    return this.get('/api/hotels');
  }

  async createHotel(data: any) {
    return this.post('/api/hotels', data);
  }

  async updateHotel(id: number, data: any) {
    return this.put(`/api/hotels/${id}`, data);
  }

  async deleteHotel(id: number) {
    return this.delete(`/api/hotels/${id}`);
  }

  async getBookings() {
    return this.get('/api/bookings');
  }

  async createBooking(data: any) {
    return this.post('/api/bookings', data);
  }

  async updateBooking(id: number, data: any) {
    return this.put(`/api/bookings/${id}`, data);
  }

  async deleteBooking(id: number) {
    return this.delete(`/api/bookings/${id}`);
  }

  async getUsers() {
    return this.get('/api/users');
  }

  async createUser(data: any) {
    return this.post('/api/users', data);
  }

  async updateUser(id: number, data: any) {
    return this.put(`/api/users/${id}`, data);
  }

  async deleteUser(id: number) {
    return this.delete(`/api/users/${id}`);
  }

  async getReviews() {
    return this.get('/api/reviews');
  }

  async createReview(data: any) {
    return this.post('/api/reviews', data);
  }

  async updateReview(id: number, data: any) {
    return this.put(`/api/reviews/${id}`, data);
  }

  async deleteReview(id: number) {
    return this.delete(`/api/reviews/${id}`);
  }

  async getPayments() {
    return this.get('/api/payments');
  }

  async createPayment(data: any) {
    return this.post('/api/payments', data);
  }

  async updatePayment(id: number, data: any) {
    return this.put(`/api/payments/${id}`, data);
  }

  async deletePayment(id: number) {
    return this.delete(`/api/payments/${id}`);
  }

  async getSupport() {
    return this.get('/api/support');
  }

  // Legacy Hotel Management methods
  async getRooms() {
    return this.get('/api/rooms');
  }

  async getGuests() {
    return this.get('/api/guests');
  }

  async getHousekeeping() {
    return this.get('/api/housekeeping');
  }

  async getMaintenance() {
    return this.get('/api/maintenance');
  }

  async getStaff() {
    return this.get('/api/staff');
  }

  async getReports() {
    return this.get('/api/reports');
  }

  // Reports methods
  async getBookingReport(filters?: any) {
    return this.get('/api/reports/booking');
  }

  async getOccupancyReport(filters?: any) {
    return this.get('/api/reports/occupancy');
  }

  async getRevenueReport(filters?: any) {
    return this.get('/api/reports/revenue');
  }

  async getPerformanceReport(filters?: any) {
    return this.get('/api/reports/performance');
  }

  async getReportsDashboardSummary() {
    return this.get('/api/reports/dashboard-summary');
  }

  async exportReport(options: any) {
    return this.post('/api/reports/export', options);
  }

  // Notifications methods
  async getNotifications() {
    return this.get('/api/notifications');
  }

  async createNotification(data: any) {
    return this.post('/api/notifications', data);
  }

  // Settings methods
  async getSettings() {
    return this.get('/api/settings');
  }

  async updateSettings(data: any) {
    return this.put('/api/settings', data);
  }

  // Static Content methods
  async getStaticPages() {
    return this.get('/api/pages/admin/all');
  }

  async getStaticPage(slug: string) {
    return this.get(`/api/pages/admin/${slug}`);
  }

  async updateStaticPage(id: number, data: any) {
    return this.put(`/api/pages/admin/${id}`, data);
  }

  async getContactInfo() {
    return this.get('/api/contact-info');
  }

  async createContactInfo(data: any) {
    return this.post('/api/contact-info', data);
  }

  async updateContactInfo(id: number, data: any) {
    return this.put(`/api/contact-info/${id}`, data);
  }

  async deleteContactInfo(id: number) {
    return this.delete(`/api/contact-info/${id}`);
  }

  async getGalleryImages() {
    return this.get('/api/gallery');
  }

  async uploadGalleryImage(formData: FormData) {
    if (this.useMockData) {
      return mockApi.post('/api/gallery/upload', formData);
    }
    return apiRequest('POST', '/api/gallery/upload', formData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  async updateGalleryImage(id: number, data: any) {
    return this.put(`/api/gallery/${id}`, data);
  }

  async deleteGalleryImage(id: number) {
    return this.delete(`/api/gallery/${id}`);
  }
}

// Export singleton instance
export const api = new ApiService();

// Export for backward compatibility
export { apiRequest }; 